import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, ShieldCheck, User, MapPin } from "lucide-react";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];

function Avatar({ member, size = "w-24 h-24", text = "text-2xl" }) {
  if (member.has_avatar) {
    return (
      <img
        src={`${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar/${member.id}`}
        alt={member.display_name}
        className={`${size} rounded-full object-cover border-2 border-nc-blue/50`}
      />
    );
  }
  return (
    <div className={`${size} rounded-full border-2 border-white/15 bg-nc-blue/10 flex items-center justify-center`}>
      <span className={`font-sans font-bold ${text} text-nc-blue`}>{member.display_name.slice(0, 1).toUpperCase()}</span>
    </div>
  );
}

export default function TeamPage() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const founder = team.find((m) => m.founder);
  const leadership = team.filter((m) => !m.founder && m.role === "admin");
  const devs = team.filter((m) => !m.founder && m.role === "dev");

  return (
    <div data-testid="team-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo
        title="Our Team — The People Behind the Studio"
        description="Meet the team at Specpixel Entertainment, founded and directed by Tanner Rozankovic in Melbourne, Australia. Industry veterans across art, cinematics and UE5 engineering."
        path="/team"
        keywords="Specpixel team, Tanner Rozankovic, Specpixel founder, game studio team Melbourne"
      />
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <p className="font-mono text-xs tracking-[0.4em] text-nc-orange uppercase mb-4">The Studio // Personnel</p>
          <h1 data-testid="team-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50">
            Our Team
          </h1>
          <p className="font-mono text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            A hand-picked crew of artists, engineers and producers behind New Citizen, Living Dead
            and our client work. Team members work under studio anonymity — the studio speaks for them.
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-14 w-full flex flex-col gap-14">
        {/* Founder */}
        {founder && (
          <motion.section data-testid="team-founder" className="border border-nc-orange/30 bg-gradient-to-br from-nc-orange/5 to-transparent relative overflow-hidden" initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <div className="absolute inset-0 grid-texture opacity-30" />
            <div className="relative p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-8">
              <div className="relative shrink-0">
                <Avatar member={founder} size="w-32 h-32 md:w-40 md:h-40" text="text-4xl" />
                <span className="absolute -bottom-2 -right-2 w-10 h-10 bg-nc-orange border-2 border-[#050505] flex items-center justify-center">
                  <Crown className="w-5 h-5 text-white" />
                </span>
              </div>
              <div className="min-w-0">
                <p className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase mb-2">Founder & Director</p>
                <h2 data-testid="founder-name" className="font-sans font-bold uppercase tracking-tight text-2xl md:text-4xl text-zinc-50">
                  {founder.display_name}
                </h2>
                <p className="font-mono text-xs text-zinc-500 mt-2 flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-nc-blue" /> Melbourne, Australia
                </p>
                {founder.bio && (
                  <p data-testid="founder-bio" className="font-mono text-sm text-zinc-400 leading-relaxed mt-4 max-w-xl">
                    {founder.bio}
                  </p>
                )}
              </div>
            </div>
          </motion.section>
        )}

        {/* Leadership */}
        {leadership.length > 0 && (
          <section>
            <p className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase mb-6">Studio Leadership</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {leadership.map((m, i) => (
                <motion.div key={m.id} data-testid={`team-leader-${i + 1}`} className="border border-white/10 bg-black/40 p-6 hover:border-nc-orange/40 transition-colors duration-300" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: i * 0.08, ease: EASE }}>
                  <Avatar member={m} size="w-20 h-20" text="text-xl" />
                  <p className="font-sans font-bold uppercase text-lg text-zinc-50 mt-4">{m.display_name}</p>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-nc-orange uppercase mt-1">{m.title || "Studio Lead"}</p>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Dev team */}
        <section>
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <p className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase mb-2">Development Team</p>
              <h2 className="font-sans font-bold uppercase tracking-tight text-xl md:text-2xl text-zinc-50">
                The Operators Building New Citizen
              </h2>
            </div>
            <span className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase border border-white/10 px-3 py-1.5">
              {devs.length} active operatives
            </span>
          </div>
          {loading ? (
            <p className="font-mono text-xs text-zinc-500 animate-pulse">Loading team…</p>
          ) : devs.length === 0 ? (
            <div className="border border-dashed border-white/15 p-12 text-center">
              <User className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
              <p className="font-mono text-xs text-zinc-500 max-w-md mx-auto leading-relaxed">
                Our development roster is being assembled. Devs appear here as they join the studio roster.
              </p>
            </div>
          ) : (
            <div data-testid="team-dev-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {devs.map((m, i) => (
                <motion.div key={m.id} data-testid={`team-dev-${i + 1}`} className="border border-white/10 bg-black/40 p-6 text-center hover:border-nc-blue/50 transition-colors duration-300 group" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: i * 0.06, ease: EASE }}>
                  <div className="flex justify-center group-hover:scale-105 transition-transform duration-300">
                    <Avatar member={m} size="w-20 h-20" text="text-xl" />
                  </div>
                  <p className="font-sans font-bold uppercase text-sm text-zinc-50 mt-4">{m.display_name}</p>
                  <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase mt-1.5 flex items-center justify-center gap-1.5">
                    <ShieldCheck className="w-3 h-3" /> {m.title || "Developer"}
                  </p>
                </motion.div>
              ))}
            </div>
          )}
          <p className="font-mono text-[10px] text-zinc-600 mt-6 leading-relaxed max-w-2xl">
            For their privacy and security, studio operatives are presented by first name only and cannot be
            contacted directly. All enquiries go through the studio — that's what we're here for.
          </p>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
